import { Box, Button, HStack, Image, Modal, Text, VStack } from 'native-base';
import React, { useState } from 'react';
import dayjs from 'dayjs';
import { faUser } from '@fortawesome/pro-solid-svg-icons/faUser';
import { Link } from '../../Common/components/Navigation';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons/faCircleCheck';
import { useTimezones } from '../../Common/hooks/useTimezones';
import { faSparkles } from '@fortawesome/pro-solid-svg-icons/faSparkles';
import { faUserDoctor } from '@fortawesome/pro-solid-svg-icons/faUserDoctor';
import { faCalendar } from '@fortawesome/pro-solid-svg-icons/faCalendar';
import { Icon } from '../../Common/components/Icon';
import { DoctorHeader } from './DoctorHeader';
import { Consult, Customer } from '../../__generated__/graphql';
import { useCustomerIHI } from '../../DoctorCustomer/hooks/useCustomerIHI';
import { DoctorCustomerEdit } from '../../DoctorCustomer/components/DoctorCustomerEdit';
import { DoctorDispenseSummary } from '../../DoctorDispenseSummary/components/DoctorDispenseSummary';
import { usePortalPermissions } from '../../Common/hooks/usePortalPermissions';
import { useDoctorCustomerResetPassword } from '../../DoctorCustomer/hooks/useDoctorCustomerResetPassword';
import { faPencil } from '@fortawesome/pro-solid-svg-icons/faPencil';
import currencyjs from 'currency.js';
import { DoctorVoiceNumber } from '../../DoctorVoice/components/DoctorVoiceNumber';
import { useCustomerEScripCheck } from '../../DoctorCustomer/hooks/useCustomerEScripCheck';
import { useUsersList } from '../../Common/hooks/useUsersList';
import { DoctorCustomerMenu } from '../../DoctorCustomer/components/DoctorCustomerMenu';
import { usePIIRedaction } from '../../Common/hooks/usePIIRedaction';
import { DoctorAppointmentCancelButton } from './DoctorAppointmentCancelButton';
import { faClock } from '@fortawesome/pro-solid-svg-icons/faClock';
import { DoctorMedicareOverview } from '../../DoctorDispenseSummary/components/DoctorMedicareOverview';
import { DoctorMHRDischarge } from '../../DoctorDispenseSummary/components/DoctorMHRDischarge';
import { DoctorMHRMedicines } from '../../DoctorDispenseSummary/components/DoctorMHRMedicines';
import { DoctorMHRDocumentSearch } from '../../DoctorDispenseSummary/components/DoctorMHRDocumentSearch';
import { DoctorMHRDocumentView } from '../../DoctorDispenseSummary/components/DoctorMHRDocumentView';

export function DoctorCustomerInfo({
  consult,
  minimal,
  customer,
}: {
  consult?: Consult;
  minimal?: boolean;
  customer?: Customer;
}) {
  const [showModal, setShowModal] = useState(false);

  const { doReset, loading: resetting } = useDoctorCustomerResetPassword(customer?.id);

  const { convertUTCDateToUserISO, convertDateToUser, convertDateToUserRegion } = useTimezones();

  const { ihi, ihiLoading, canViewIHI } = useCustomerIHI(customer?.id);

  const { precheckData, loadingPreCheck } = useCustomerEScripCheck(customer?.id);

  const { hasPermission, isDoctor } = usePortalPermissions();

  const { findUser } = useUsersList();

  const { redact } = usePIIRedaction();

  const [ viewDocumentId, setViewDocumentId ] = useState<string | null | undefined>(null);
  const [ viewDocumentRepoId, setViewDocumentRepoId ] = useState<string | null | undefined>(null);

  const onViewDocumentCLicked = (documentID: string | null | undefined, repositoryID: string | null | undefined) => {
    setViewDocumentId(documentID);
    setViewDocumentRepoId(repositoryID ?? null);
  }

  const brandURL = () => {
    if (customer?.brand === 'leafdoctors')
      return require('../../Assets/LeafDoctors/LD_Logo_Mark_Kelp.png');

    return require('../../Assets/onlinedoctor.png');
  };

  if (!customer) return null;

  return (
    <>
      <Box>
        {!minimal && <DoctorHeader icon={faUser} title={'Customer Request'} />}

        <VStack>
          <Link to={`/customers/${customer?.id}`}>
            <HStack pb={2} mb={2} borderBottomWidth={1} borderBottomColor={'gray.200'}>
              <Text flex={1}>Name:</Text>
              <HStack space={2} flex={2} alignItems={'center'} justifyItems={'center'}>
                <Image alt={customer?.brand} borderRadius={'sm'} source={brandURL()} w={6} h={6} />
                <Text fontWeight={600}>{customer?.name}</Text>
              </HStack>
            </HStack>
          </Link>

          {consult && (
            <HStack pb={2} mb={2} borderBottomWidth={1} borderBottomColor={'gray.200'}>
              <Text flex={1}>Requested At:</Text>

              <Text flex={2}>
                {convertUTCDateToUserISO(consult?.requestedAt)?.format('DD/MM/YYYY hh:mm a')}
              </Text>
            </HStack>
          )}

          {consult && (
              <HStack pb={2} mb={2} borderBottomWidth={1} borderBottomColor={'gray.200'}>
                <Text flex={1}>Location:</Text>
                {!consult?.clinic && <Text flex={2}>Telehealth</Text>}
                {consult?.clinic && <Text flex={2}>{consult.clinic.name}</Text>}
              </HStack>
          )}

          {consult?.appointment && (
            <>
              <HStack pb={2} mb={2} borderBottomWidth={1} borderBottomColor={'gray.200'}>
                <Text flex={1}>Appointment:</Text>
                <VStack space={1} flex={2}>
                  <Text>
                    <Icon size={'sm'} icon={faCalendar} />{' '}
                    {convertDateToUser(consult.appointment.date)?.format('DD/MM/YYYY hh:mm a')}
                    {' with '}
                    {findUser(consult?.appointment?.userId)?.name}
                  </Text>

                  {hasPermission('cancel.appointment') && <DoctorAppointmentCancelButton appointmentID={consult?.id} />}
                </VStack>
              </HStack>
              <HStack pb={2} mb={2} borderBottomWidth={1} borderBottomColor={'gray.200'}>
                <Text flex={1}>Patient time:</Text>
                <VStack space={1} flex={2}>
                  <Text>
                    <Icon size={'sm'} icon={faClock} />{' '}
                    {convertDateToUserRegion(consult.appointment.date, customer.state)?.format('h:mm a z')}
                  </Text>
                </VStack>
              </HStack>
            </>
          )}
          {consult && (
            <HStack pb={2} mb={2} borderBottomWidth={1} borderBottomColor={'gray.200'}>
              <Text flex={1}>Status:</Text>

              {consult.state === 'PENDING' && (
                <>
                  <VStack flex={2}>
                    <Text fontWeight={600}>
                      <Icon icon={faSparkles} /> Pending
                    </Text>
                  </VStack>
                </>
              )}

              {consult.state === 'INPROGRESS' && (
                <>
                  <VStack flex={2}>
                    <Text fontWeight={600}>
                      <Icon icon={faUserDoctor} /> In Progress
                    </Text>
                  </VStack>
                </>
              )}

              {consult.state === 'COMPLETED' && (
                <>
                  <VStack flex={2}>
                    <Text fontWeight={600}>
                      <Icon icon={faCircleCheck} /> Completed {consult?.completedAt}
                    </Text>
                    <Text>By {consult?.doctorAssigned?.name}</Text>
                    <Text>
                      At{' '}
                      {convertUTCDateToUserISO(consult?.doctorEndedAt)?.format('DD/MM/YYYY hh:mm a')}
                    </Text>
                  </VStack>
                </>
              )}
            </HStack>
          )}

          {consult && (
            <HStack pb={2} mb={2} borderBottomWidth={1} borderBottomColor={'gray.200'}>
              <Text flex={1}>Billed:</Text>

              <VStack flex={2}>
                <Text
                  fontWeight={consult?.billed ? null : 600}
                  color={!consult?.billed ? 'red.500' : null}
                >
                  {consult?.billed ? 'Paid' : 'Unpaid'}{' '}
                  {currencyjs(consult?.billedAmount ?? 0).format()}
                </Text>
              </VStack>
            </HStack>
          )}

          <HStack pb={2} mb={2} borderBottomWidth={1} borderBottomColor={'gray.200'}>
            <Text flex={1}>DOB:</Text>
            <Text flex={2}>
              {customer?.dob &&
                `${customer?.dob} - Age ${dayjs().from(
                  dayjs(customer?.dob, 'DD/MM/YYYY', true),
                  true,
                )}`}
            </Text>
          </HStack>

          <HStack pb={2} mb={2} borderBottomWidth={1} borderBottomColor={'gray.200'}>
            <Text flex={1}>Gender:</Text>
            <Text flex={2}>{customer?.gender}</Text>
          </HStack>

          {!redact && !minimal && (
            <HStack pb={2} mb={2} borderBottomWidth={1} borderBottomColor={'gray.200'}>
              <Text flex={1}>eMail:</Text>
              <Text flex={2}>{customer?.email}</Text>
            </HStack>
          )}

          {!redact && (
            <HStack
              alignItems={'center'}
              pb={2}
              mb={2}
              borderBottomWidth={minimal ? 0 : 1}
              borderBottomColor={'gray.200'}
            >
              <Text flex={1}>Mobile:</Text>
              <Box flex={2}>
                <DoctorVoiceNumber
                  number={customer?.mobile}
                  customerId={customer?.id}
                  customerName={customer?.name}
                  consult={consult?.id}
                />
              </Box>
            </HStack>
          )}

          {!redact && !minimal && (
            <HStack pb={2} mb={2} borderBottomWidth={1} borderBottomColor={'gray.200'}>
              <Text flex={1}>Medicare:</Text>
              <Text flex={2}>
                {customer?.medicarenumber} #{customer?.medicareref}
              </Text>
            </HStack>
          )}

          {!minimal && (
            <HStack
              pb={2}
              alignItems={'center'}
              mb={2}
              borderBottomWidth={1}
              borderBottomColor={'gray.200'}
            >
              <Text flex={1}>eScript Status:</Text>

              <Box flex={2}>
                {!loadingPreCheck && precheckData && precheckData?.preCheckPrescription?.ok && (
                  <Text
                    textAlign={'center'}
                    fontWeight={600}
                    borderRadius={'sm'}
                    flex={1}
                    numberOfLines={1}
                    size={'xs'}
                    h={8}
                    p={1}
                    bg={'green.200'}
                  >
                    {`eScript ready`}
                  </Text>
                )}

                {!loadingPreCheck && precheckData && !precheckData?.preCheckPrescription?.ok && (
                  <Text
                    size={'xs'}
                    textAlign={'center'}
                    fontWeight={600}
                    numberOfLines={1}
                    flex={1}
                    borderRadius={'sm'}
                    h={8}
                    p={1}
                    bg={'red.200'}
                  >
                    {`${precheckData?.preCheckPrescription?.message}`}
                  </Text>
                )}
              </Box>
            </HStack>
          )}

          {!redact && !minimal && canViewIHI && (
            <HStack pb={2} mb={2} borderBottomWidth={1} borderBottomColor={'gray.200'}>
              <Text flex={1}>IHI:</Text>
              <Box flex={2}>{!ihiLoading && <Text>{ihi ?? 'Not found'}</Text>}</Box>
            </HStack>
          )}

          {!redact && !minimal && (
            <HStack pb={2} mb={2} borderBottomWidth={1} borderBottomColor={'gray.200'}>
              <Text flex={1}>Address:</Text>
              <Text flex={2}>
                {customer?.street}, {customer?.suburb} {customer?.state} {customer?.postcode}
              </Text>
            </HStack>
          )}

            {!minimal && (
                <HStack pb={2} mb={2} borderBottomWidth={1} borderBottomColor={'gray.200'}>
                    <Text flex={1}>Found us:</Text>
                    <Text flex={2}>{customer?.hearaboutus}</Text>
                </HStack>
            )}

            {!minimal && (
                <HStack pb={2} mb={2} borderBottomWidth={1} borderBottomColor={'gray.200'}>
                    <Text flex={1}>Referral code:</Text>
                    <Text flex={2}>{!!customer?.referredByCode ? customer?.referredByCode : "None"}</Text>
                </HStack>
            )}

            {!redact && !minimal && (
            <HStack pb={2} mb={2} borderBottomWidth={1} borderBottomColor={'gray.200'}>
              <Text flex={1}>Reported Med History:</Text>
              <Text flex={2}>{customer?.medicalhistory}</Text>
            </HStack>
          )}

          {!redact && !minimal && (
            <HStack pb={2} mb={2} borderBottomWidth={1} borderBottomColor={'gray.200'}>
              <Text flex={1}>Reported Medications:</Text>
              <Text flex={2}>{customer?.medications}</Text>
            </HStack>
          )}

          {!redact && !minimal && (
            <HStack pb={2} mb={2} borderBottomWidth={1} borderBottomColor={'gray.200'}>
              <Text flex={1}>Reported Allergies:</Text>
              <Text flex={2}>{customer?.allergies}</Text>
            </HStack>
          )}

          {!minimal && (
            <VStack space={2}>
              {!redact && (
                <Button
                  leftIcon={<Icon icon={faPencil} />}
                  size={'sm'}
                  variant={'outline'}
                  onPress={() => setShowModal(true)}
                >
                  Edit Customer
                </Button>
              )}

              <DoctorCustomerMenu customerID={customer?.id} />

              {hasPermission('notes') && isDoctor && (
                <>
                  <DoctorMHRDocumentSearch customerID={customer?.id} />
                  <DoctorDispenseSummary customerID={customer?.id} onLoadDocumentCLicked={onViewDocumentCLicked} />
                  <DoctorMHRMedicines customerID={customer?.id} onLoadDocumentCLicked={onViewDocumentCLicked} />
                  <DoctorMedicareOverview customerID={customer?.id} onLoadDocumentCLicked={onViewDocumentCLicked} />
                  <DoctorMHRDischarge customerID={customer?.id} onLoadDocumentCLicked={onViewDocumentCLicked} />
                  <Modal isOpen={!!viewDocumentId} size={'full'} onClose={() => setViewDocumentId(null)}>
                    {!!viewDocumentId && <DoctorMHRDocumentView customerID={customer.id} documentID={viewDocumentId??""} repositoryID={viewDocumentRepoId??""} onBackClicked={() => onViewDocumentCLicked(null, null)} onLoadDocumentCLicked={onViewDocumentCLicked} />}
                  </Modal>
                  </>
              )}
            </VStack>
          )}
        </VStack>
      </Box>

      <Modal isOpen={showModal} size={'xl'} onClose={() => setShowModal(false)}>
        {showModal && (
          <DoctorCustomerEdit customerID={customer?.id} closeModal={() => setShowModal(false)} />
        )}
      </Modal>
    </>
  );
}
